import { isErr } from "@/lib/errors";
import { createFileRoute } from "@tanstack/react-router";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Building2, Mail, Settings, AppWindow } from "lucide-react";
import { StorageUtility } from "@/lib/storage";
import { VolumeApi } from "@/lib/volume-api";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardContent, CardTitle } from "@/components/ui/card";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import { Separator } from "@/components/ui/separator";
import clsx from "clsx";
import { redirectToSignIn } from "@/lib/auth";
import { ConfirmationOfPayerToggle } from "./-components/confirmation-of-payer-toggle";
import { ManageOptimusCustomer } from "./-components/manage-optimus-customer";
import { PayinAccounts } from "./-components/payin-accounts";

export const Route = createFileRoute(
  "/(app)/_admin-layout/admin_/merchants_/$merchantId",
)({
  component: () => <MerchantDetailComponent />,
  loader: async ({ context, params }) => {
    const volumeApi = new VolumeApi(context.accessToken, "");
    const merchantsResponse = await volumeApi.getMerchant({
      merchantId: params.merchantId,
    });

    if (isErr(merchantsResponse)) {
      if (merchantsResponse.type === "UNAUTHORIZED") {
        toast.error(
          "There was an error fetching merchant details, please sign in again.",
        );
        throw redirectToSignIn(false);
      }
      throw new Error("Failed to fetch merchant details");
    }

    return merchantsResponse;
  },
});

const MerchantDetailComponent = () => {
  const merchant = Route.useLoaderData();
  const navigate = Route.useNavigate();

  const handleViewAsPortal = async () => {
    StorageUtility.setItem("v_merchantId_v1", merchant.merchantId);
    await navigate({
      to: "/payments/payin",
    });
  };

  const handleBackToList = () => {
    navigate({
      to: "/admin",
    });
  };

  return (
    <div className="min-h-screen">
      {/* Header row with grid alignment */}
      <div className="flex items-center space-x-4 max-w-[100rem] mx-auto px-6 my-2">
        <Button
          variant="outline"
          size="sm"
          className="bg-white text-gray-700"
          onClick={handleBackToList}
        >
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to Merchants
        </Button>
      </div>
      <div className="max-w-[100rem] mx-auto px-6 grid grid-cols-1 lg:grid-cols-3 gap-6 items-center">
        {/* Left column header: Back button and merchant name */}
        <div className="flex flex-col lg:col-span-1">
          <h1 className="text-2xl font-semibold text-gray-900 mt-2 break-words">
            {merchant.companyName}
          </h1>
          <Separator className="mb-4 mt-2 hidden lg:block" />
        </div>
        {/* Right column header: Applications heading */}
        <div className="hidden lg:flex flex-col lg:col-span-2">
          <div className="flex items-center gap-2">
            <AppWindow className="h-5 w-5 text-blue-600" />
            <h2 className="text-2xl font-semibold text-gray-900">
              Applications
            </h2>
          </div>
          <Separator className="mb-4 mt-4" />
        </div>
      </div>

      <div className="max-w-[100rem] mx-auto px-6 pt-0 pb-3">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Merchant Info & Actions */}
          <div className="lg:col-span-1 space-y-6">
            {/* Basic Information */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center text-lg">
                  <Building2 className="w-5 h-5 mr-2 text-blue-600" />
                  Basic Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Company Name
                  </label>
                  <p className="text-sm text-gray-900">
                    {merchant.companyName}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Merchant ID
                  </label>
                  <p className="text-sm text-gray-900 font-mono">
                    {merchant.merchantId}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Email
                  </label>
                  <div className="flex items-center">
                    <Mail className="w-4 h-4 mr-2 text-gray-400" />
                    <p className="text-sm text-gray-900">{merchant.email}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Actions */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Actions</CardTitle>
              </CardHeader>
              <CardContent>
                <Button
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                  onClick={handleViewAsPortal}
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Impersonate {merchant.companyName}
                </Button>
              </CardContent>
            </Card>

            {/* Can add this if needed */}
            {/* <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">All Merchant Info</CardTitle>
              </CardHeader>
              <CardContent>
                <MerchantTableRow merchant={merchant} />
              </CardContent>
            </Card> */}
          </div>

          {/* Right Column - Applications */}
          <div className="lg:col-span-2">
            <div className="">
              <Accordion
                type="multiple"
                className="space-y-4"
                defaultValue={
                  merchant.applications.length === 1
                    ? [merchant.applications[0].applicationId]
                    : undefined
                }
              >
                {merchant.applications.map((application) => (
                  <AccordionItem
                    key={application.applicationId}
                    value={application.applicationId}
                    className="border border-gray-200 rounded-lg bg-white px-4"
                  >
                    <AccordionTrigger className="w-full">
                      <div className="flex items-center justify-between p-4 w-full">
                        <div className="flex items-center space-x-3">
                          <div className="text-left">
                            <p className="font-medium text-gray-900">
                              {application.applicationName}
                            </p>
                            <p className="text-sm text-gray-500">
                              ID: {application.applicationId}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Badge
                            variant="secondary"
                            className={clsx(
                              "bg-green-100 text-green-800",
                              application.hasVirtualAccountsEnabled
                                ? "bg-green-100 text-green-800"
                                : "bg-red-100 text-red-800",
                            )}
                          >
                            Virtual Accounts:{" "}
                            {application.hasVirtualAccountsEnabled
                              ? "Yes"
                              : "No"}
                          </Badge>
                          <Badge variant="outline">
                            {application.payeeDetailsPerCurrency?.length || 0}{" "}
                            Payin Accounts
                          </Badge>
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="pb-4 space-y-4">
                        <ConfirmationOfPayerToggle application={application} />
                        <PayinAccounts application={application} />
                        <div className="mt-6">
                          <div className="flex items-center gap-2 mb-2">
                            <Settings className="h-5 w-5 text-green-600" />
                            <h3 className="text-lg font-medium text-gray-900">
                              Optimus Virtual Account Configuration
                            </h3>
                          </div>
                          <div>
                            <ManageOptimusCustomer
                              application={application}
                              merchantId={merchant.merchantId}
                            />
                          </div>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
